<template>
    <div>
        <el-dialog title="具体算法" width="600px" center :visible.sync="dlg.visible" :close-on-click-modal="false">
            <div class="modalWrap">
                <p v-if="/^header|^header\d+$/.test(dlg.type)">原始公式：{{ headData }}</p>
                <p v-if="/^column|^column\d+$/.test(dlg.type)">具体算法：{{ columnData }}</p>
            </div>
            <div slot="footer" class="dialog-footer">
                <el-button type="primary" @click="closeDlg">知道了</el-button>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import api from '@/service/api.js';

export default {
    props: {
        dlg: {
            required: true,
        }
    },
    data() {
        return {
            headData: '',
            columnData: '',
            hospitalCode: '',
            datasources: ''
        }
    },

    async mounted() {
        this.hospitalCode = await api.getHospitalCode();
        this.datasources = this.$route.query.datasources;
        this.setFormulaData();
    },

    computed: {
        // 计算属性简化重复逻辑
        visitType() {
            return this.datasources === '1' ? '就诊' : '出院';
        },

        hepatitisType() {
            return this.dlg.dataType === 'HBV' ? '乙肝' : '丙肝';
        },

        dnaType() {
            return this.dlg.dataType === 'HBV' ? '乙肝DNA' : '丙肝RNA';
        }
    },

    methods: {
        closeDlg() {
            this.dlg.visible = false;
        },

        setFormulaData() {
            const formulaMap = this.getFormulaMap();
            const key = `${this.dlg.propname}_${this.dlg.type}`;
            const formula = formulaMap[key];

            if (formula) {
                if (this.dlg.type.startsWith('header')) {
                    this.headData = formula;
                } else if (this.dlg.type.startsWith('column')) {
                    this.columnData = formula;
                }
            }
        },

        getFormulaMap() {
            const { data = {} } = this.dlg;

            return {
                // 肝癌相关公式
                'liverCancer_header': `肝癌筛查率 = 甲胎蛋白检验人数 / ${this.hepatitisType}人数 * 100%`,
                'liverCancer_header1': `病毒抑制率 = ${this.dnaType}测定阴性人数 / ${this.dnaType}测定人数 * 100%`,
                'liverCancer_column': `肝癌筛查率 ${data.lcsRate} = 甲胎蛋白检验人数 ${data.afpCount} / ${this.hepatitisType}人数 ${data.patientGroupCount} * 100%`,
                'liverCancer_column1': `病毒抑制率 ${data.viRate} = ${this.dnaType}测定阴性人数 ${data.hepatitisNegativeCount} / ${this.dnaType}测定人数 ${data.hepatitisCount} * 100%`,

                // 排期相关公式 (schedule, schedule2)
                'schedule_header': `已排期率 =（已排期人次+过期已排人次）/ ${this.visitType}人次 *100%`,
                'schedule2_header': `已排期率 =（已排期人次+过期已排人次）/ ${this.visitType}人次 *100%`,
                'schedule_header1': '已排期及时率 = 已排期人次 /（已排期人次+过期已排人次）*100%',
                'schedule2_header1': '已排期及时率 = 已排期人次 /（已排期人次+过期已排人次）*100%',
                'schedule_header2': `勿访率 = （勿访人次+过期勿访人次）/ ${this.visitType}人次 *100%`,
                'schedule2_header2': `勿访率 = （勿访人次+过期勿访人次）/ ${this.visitType}人次 *100%`,
                'schedule_column': `已排期率 ${data.scheduleRate} =（已排期人次${data.scheduledTotal} + 过期已排人次${data.scheduledOverdueTotal}）/ ${this.visitType}人次${data.outHospitalTotal} *100%`,
                'schedule2_column': `已排期率 ${data.scheduleRate} =（已排期人次${data.scheduledTotal} + 过期已排人次${data.scheduledOverdueTotal}）/ ${this.visitType}人次${data.outHospitalTotal} *100%`,
                'schedule_column1': `已排期及时率 ${data.timelySchedulingRate} = 已排期人次${data.scheduledTotal} /（已排期人次${data.scheduledTotal} + 过期已排人次${data.scheduledOverdueTotal}）*100%`,
                'schedule2_column1': `已排期及时率 ${data.timelySchedulingRate} = 已排期人次${data.scheduledTotal} /（已排期人次${data.scheduledTotal} + 过期已排人次${data.scheduledOverdueTotal}）*100%`,
                'schedule_column2': `勿访率 ${data.doNotVisitRate} =（勿访人次${data.doNotVisitTotal} + 过期勿访人次${data.doNotVisitOverdueTotal}）/ ${this.visitType}人次${data.outHospitalTotal} *100%`,
                'schedule2_column2': `勿访率 ${data.doNotVisitRate} =（勿访人次${data.doNotVisitTotal} + 过期勿访人次${data.doNotVisitOverdueTotal}）/ ${this.visitType}人次${data.outHospitalTotal} *100%`,

                // 复诊相关公式 (return, return3, returnManage)
                'return_header': `预约率 = 预约复诊人次 / ${this.visitType}总人次*100%`,
                'return3_header': `预约率 = 预约复诊人次 / ${this.visitType}总人次*100%`,
                'returnManage_header': `预约率 = 预约复诊人次 / ${this.visitType}总人次*100%`,
                'return_header1': '履约率 = 已复诊人次 /（预约复诊人次-未到复诊时间人次）*100%',
                'return3_header1': '履约率 = 已复诊人次 /（预约复诊人次-未到复诊时间人次）*100%',
                'returnManage_header1': '履约率 = 已复诊人次 /（预约复诊人次-未到复诊时间人次）*100%',
                'return_column': `预约率【${data.reservationRate}】= 预约复诊人次【${data.returnVisitTotal}】/ ${this.visitType}总人次【${data.totalNumbers}】*100%`,
                'return3_column': `预约率【${data.reservationRate}】= 预约复诊人次【${data.returnVisitTotal}】/ ${this.visitType}总人次【${data.totalNumbers}】*100%`,
                'returnManage_column': `预约率【${data.reservationRate}】= 预约复诊人次【${data.returnVisitTotal}】/ ${this.visitType}总人次【${data.totalNumbers}】*100%`,
                'return_column1': `履约率【${data.agreementRate}】= 已复诊人次【${data.actualTotal}】/（预约复诊人次【${data.returnVisitTotal}】 - 未到复诊时间人次【${data.toBeTotal}】）*100%`,
                'return3_column1': `履约率【${data.agreementRate}】= 已复诊人次【${data.actualTotal}】/（预约复诊人次【${data.returnVisitTotal}】 - 未到复诊时间人次【${data.toBeTotal}】）*100%`,
                'returnManage_column1': `履约率【${data.agreementRate}】= 已复诊人次【${data.actualTotal}】/（预约复诊人次【${data.returnVisitTotal}】 - 未到复诊时间人次【${data.toBeTotal}】）*100%`,

                // 随访相关公式 - 根据医院代码区分
                ...this.getFollowupFormulas(),

                // 住院管理相关公式
                'hospitalIn_header': '管理率 = （管理中人次+已完成人次) / 入院人次 *100%',
                'hospitalIn_header1': '评估完成率 = 已评估人次 /（管理中人次 + 已完成人次) *100%',
                'hospitalIn_header2': '处方生成率 = 处方生成人次/（管理中人次+已完成人次）*100%',
                'hospitalIn_header3': '已开具处方占比 = 处方开具人次 / (管理中+已完成)人次 *100%',
                'hospitalIn_column': `管理率【${data.managementRate}】=（管理中人次【${data.finishAllocated}】+ 已完成人次【${data.doneAllocated}】）/ 入院人次【${data.countId}】*100%`,
                'hospitalIn_column1': `评估完成率【${data.evaluatorsRate}】= 已评估人次【${data.countEvaluators}】/（管理中人次【${data.finishAllocated}】+ 已完成人次【${data.doneAllocated}】）*100%`,
                'hospitalIn_column2': `处方生成率【${data.prescriptionRate}】= 处方生成人次【${data.countPrescription}】/（管理中人次【${data.finishAllocated}】+ 已完成人次【${data.doneAllocated}】）*100%`,

                // 健商相关公式
                'HQ_header': '提升率=健商提升人次/两次完成人次*100%',
                'HQ_header1': '有效健商评估率=两项完成人次/入院健商人次*100%',
                'HQ_column': `提升率【${data.healthQuotientUpRate}】=健商提升人次【${data.avgUpTotal}】/两次完成人次【${data.twoTotal}】*100%`,
                'HQ_column1': `有效健商评估率【${data.validRate}】=两项完成人次【${data.twoTotal}】/入院健商人次【${data.inTotal}】*100%`
            };
        },

        getFollowupFormulas() {
            const { data = {} } = this.dlg;
            const baseFormulas = {};
            const propNames = ['follow', 'follow2', 'followManage'];

            // 为每个propName添加公式
            propNames.forEach(propName => {
                // 通用随访公式
                baseFormulas[`${propName}_header`] = '随访完成率 = 有效已随访人次 + 无效已随访人次 / (应随访人次 - 未到随访时间人次 - 终止随访人次)*100%';
                baseFormulas[`${propName}_header5`] = '终止随访率 = 终止随访人次 / (应随访人次 - 未到随访时间人次)*100%';
                baseFormulas[`${propName}_column`] = `随访完成率【${data.followupCompletionRate || '--'}】= 有效已随访人次【${data.validTotal || 0}】+ 无效已随访人次【${data.invalidTotal || 0}】 / (应随访人次【${data.followupTotal || 0}】 - 未到随访时间人次【${data.notFollowTimeTotal || 0}】 - 终止随访人次【${data.shutdownTotal || 0}】)*100%`;
                baseFormulas[`${propName}_column5`] = `终止随访率【${data.shutdownRate || '--'}】= 终止随访人次【${data.shutdownTotal || 0}】 / (应随访人次【${data.followupTotal || 0}】 - 未到随访时间人次【${data.notFollowTimeTotal || 0}】)*100%`;

                // 根据医院代码添加不同的header1公式
                if (this.hospitalCode === 'SD_LY1') {
                    baseFormulas[`${propName}_header1`] = "\n\n首次有效随访率=首次有效随访人次/首次应随访人次*100%\n\n二次有效随访率=二次有效随访人次/二次应随访人次*100%\n\n三次及以上有效随访率=三次及以上有效随访人次/三次及以上应随访人次*100%\n\n合计有效随访率=合计有效随访人次/合计应随访人次*100%";
                    baseFormulas[`${propName}_column1`] = `首次有效随访率【${data.followupCompletionFirstRate || '--'}】=首次有效随访人次【${data.followupFinishedFirstTotal || 0}】/首次应随访人次【${data.followupFirstTotal || 0}】*100%`;
                    baseFormulas[`${propName}_column2`] = `二次有效随访率【${data.followupCompletionSecondRate || '--'}】=二次有效随访人次【${data.followupFinishedSecondTotal || 0}】/二次应随访人次【${data.followupSecondTotal || 0}】*100%`;
                    baseFormulas[`${propName}_column3`] = `三次及以上有效随访率【${data.followupCompletionThreeMoreRate || '--'}】=三次及以上有效随访人次【${data.followupFinishedThreeMoreTotal || 0}】/三次及以上应随访人次【${data.followupThreeMoreTotal || 0}】*100%`;
                    baseFormulas[`${propName}_column4`] = `合计有效随访率【${data.validRate || '--'}】=合计有效随访人次【${data.validTotal || 0}】/合计应随访人次【${data.followupTotal || 0}】*100%`;
                } else {
                    baseFormulas[`${propName}_header1`] = '有效随访率 = 有效已随访人次 / (有效已随访人次 + 无效已随访人次)*100%';
                    baseFormulas[`${propName}_column1`] = `有效随访率【${data.validRate || '--'}】= 有效已随访人次【${data.validTotal || 0}】/ (有效已随访人次【${data.validTotal || 0}】 + 无效已随访人次【${data.invalidTotal || 0}】)*100%`;
                }
            });

            return baseFormulas;
        }
    }
}
</script>

<style lang="less" scoped>
@import '@/assets/style/var';
</style>
